import { STORAGE } from '../constants';
import { safeJsonParse } from '../../shared/utils';
import type { InputDevice, PlayerId } from '../../shared/types';

export interface PlayerInputSettings {
  device: InputDevice;
  gamepadIndex: number;
}

export interface SettingsData {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  fullscreen: boolean;
  showFPS: boolean;
  screenShake: boolean;
  player1Input: PlayerInputSettings;
  player2Input: PlayerInputSettings;
  player3Input: PlayerInputSettings;
  player4Input: PlayerInputSettings;
}

const DEFAULT_SETTINGS: SettingsData = {
  masterVolume: 0.8,
  musicVolume: 0.7,
  sfxVolume: 0.8,
  fullscreen: false,
  showFPS: false,
  screenShake: true,
  player1Input: {
    device: 'keyboard',
    gamepadIndex: 0,
  },
  player2Input: {
    device: 'gamepad',
    gamepadIndex: 0,
  },
  player3Input: {
    device: 'gamepad',
    gamepadIndex: 1,
  },
  player4Input: {
    device: 'gamepad',
    gamepadIndex: 2,
  },
};

class SettingsStateManager {
  private data: SettingsData;

  constructor() {
    this.data = this.load();
  }

  private load(): SettingsData {
    const stored = localStorage.getItem(STORAGE.SETTINGS);
    return { ...DEFAULT_SETTINGS, ...safeJsonParse<Partial<SettingsData>>(stored, {}) };
  }

  save(): void {
    localStorage.setItem(STORAGE.SETTINGS, JSON.stringify(this.data));
  }

  get(): SettingsData {
    return { ...this.data };
  }

  set<K extends keyof SettingsData>(key: K, value: SettingsData[K]): void {
    this.data[key] = value;
    this.save();
  }

  setPlayerInput(player: PlayerId, settings: PlayerInputSettings): void {
    switch (player) {
      case 'P1': this.data.player1Input = settings; break;
      case 'P2': this.data.player2Input = settings; break;
      case 'P3': this.data.player3Input = settings; break;
      case 'P4': this.data.player4Input = settings; break;
    }
    this.save();
  }

  getPlayerInput(player: PlayerId): PlayerInputSettings {
    switch (player) {
      case 'P1': return { ...this.data.player1Input };
      case 'P2': return { ...this.data.player2Input };
      case 'P3': return { ...this.data.player3Input };
      case 'P4': return { ...this.data.player4Input };
    }
  }

  reset(): void {
    this.data = { ...DEFAULT_SETTINGS };
    this.save();
  }

  // Volume helpers
  getMasterVolume(): number {
    return this.data.masterVolume;
  }

  getMusicVolume(): number {
    return this.data.masterVolume * this.data.musicVolume;
  }

  getSfxVolume(): number {
    return this.data.masterVolume * this.data.sfxVolume;
  }
}

export const SettingsState = new SettingsStateManager();
